import { ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { featureIsEnabled, useFeatures } from './features';
import { Messages, useBundles } from './messages';
import { toArbId } from '../utils/arbIds';
import {
  LOCALIZATION,
  WEBSITE_COMMON_BUNDLE,
  WEBSITE_RICH_TEXT_EDITOR_BUNDLE,
} from '../utils/constants';

/**
 * i18next namespace → the RedVelvetTranslations bundles holding its strings, in the order they are
 * searched. Names aren't 1:1 (translation→common, signup→signups), and a namespace can span several
 * bundles: the default `translation` namespace is split by feature across six of them, because the
 * source strings are grouped by the feature they belong to rather than by the namespace the website
 * reads them from — the bulk-update strings live in the grant bundle, the shift table's in the shifts
 * bundle. Every bundle compiles into the same pack file per locale, so listing several costs no extra
 * fetch, and no id appears in two of them, so the order within a namespace never decides a string.
 *
 * Exported for the pack contract test, which checks the compiled artifact actually ships every
 * bundle named here — a rename in RedVelvetTranslations is otherwise invisible until the UI is blank.
 */
export const NS_TO_BUNDLES: Record<string, string[]> = {
  translation: [
    WEBSITE_COMMON_BUNDLE,
    'website.eventViewing',
    'website.grant',
    'website.search',
    'website.shifts',
    'options.daysOfWeek',
    'cloudscape.common',
  ],
  events: ['website.events'],
  attendance: ['website.attendance'],
  shifts: ['website.shifts'],
  signup: ['website.signups'],
  activity: ['website.activity'],
  ambassadors: ['website.ambassadors'],
  coordinatorManageWaitlist: ['website.waitlist'],
  richTextEditor: [WEBSITE_RICH_TEXT_EDITOR_BUNDLE],
};

// Returns undefined for an unmapped namespace; the dev throw surfaces it, and in prod the caller
// degrades to the i18next fallback.
function bundlesFor(ns: string): string[] | undefined {
  const bundles = NS_TO_BUNDLES[ns];
  if (!bundles && process.env.NODE_ENV !== 'production') {
    throw new Error(`useLocalizedText: no bundle mapped for i18next namespace "${ns}".`);
  }
  return bundles;
}

export type TextArgs = Record<string, unknown>;

/** The namespace whose bundles back every lookup as a last resort, matching i18next's fallbackNS. */
const DEFAULT_NAMESPACE = 'translation';

/** A t() that resolves a key to a string. Enough for a helper that only reads text. */
export type TextFunction = (key: string | string[], args?: TextArgs) => string;

export type LocalizedText = {
  /**
   * Same shape as react-i18next's t(): a key (optionally `ns:`-prefixed), plus optional args. An
   * array of keys resolves to the first one that has a value, as i18next resolves it.
   */
  t: TextFunction;
  /**
   * The rich-text analogue of t(), for a message with paired inline tags (`<b>…</b>`): pass a
   * function per tag in `args` and get back the nodes they formatted to. Returns undefined when ARB
   * has nothing to render — flag off, pack missing, or id absent — so the caller can fall back to
   * react-i18next. Prefer <LocalizedTrans>, which does that falling back for you.
   */
  rich: (key: string, args?: TextArgs) => ReactNode | undefined;
  isLoading: boolean;
};

/** Splits an optionally `ns:`-prefixed key: an unprefixed key belongs to the primary namespace. */
function splitKey(key: string, primaryNs: string): { ns: string; rest: string } {
  const sep = key.indexOf(':');
  return sep === -1
    ? { ns: primaryNs, rest: key }
    : { ns: key.slice(0, sep), rest: key.slice(sep + 1) };
}

/**
 * The bundles a key reads from, in search order, for callers that read ARB messages directly instead
 * of through t() (tOptions). Undefined unless the key's namespace is one of `namespaces`, matching how
 * t() resolves a `ns:` prefix — an undeclared namespace degrades to i18next rather than quietly
 * reaching a bundle t() would not have read.
 */
export function bundlesForKey(key: string, namespaces: string[]): string[] | undefined {
  const { ns } = splitKey(key, namespaces[0]);
  return namespaces.includes(ns) ? NS_TO_BUNDLES[ns] : undefined;
}

/**
 * Reads UI text from RedVelvetTranslations or the legacy i18next bundles, chosen by the Localization
 * flag. `namespaces` is the same array passed to useTranslation today: a plain key is searched in
 * each of them in turn, and a `ns:key` reaches that namespace's bundles. Both systems read every
 * render; only t() switches. When the flag is off, or ARB has no value (missing id / failed pack),
 * t() falls back to the English i18next string, so flag-on is never worse than flag-off.
 *
 * Contract: a `ns:` prefix only reaches ARB when `ns` is one of the `namespaces` passed here (that's
 * the set of bundles loaded). A prefix for an unlisted or unmapped namespace falls back to i18next
 * and never throws — so declare every namespace a component's keys reference.
 */
export function useLocalizedText(namespaces: string[]): LocalizedText {
  const { t: t18 } = useTranslation(namespaces);
  // The default namespace's bundles are always loaded and always searched last, mirroring i18next's
  // own `fallbackNS: 'translation'`: a component that declares only its feature's namespace still
  // reaches the shared strings (the Cloudscape table, pagination and filter labels) rather than
  // reading blank and falling back to English for every one of them.
  const searchOrder = namespaces.includes(DEFAULT_NAMESPACE)
    ? namespaces
    : [...namespaces, DEFAULT_NAMESPACE];
  // Deduplicated: a namespace's bundles can overlap the default namespace's (website.shifts is in
  // both), and a bundle listed twice would be loaded twice and read twice on every miss.
  const bundles = Array.from(new Set(searchOrder.flatMap((ns) => bundlesFor(ns) ?? [])));
  const readers = useBundles(bundles);
  const useArb = featureIsEnabled(LOCALIZATION, useFeatures(), useSearchParams()[0]);
  // searchOrder is a fresh array each render; this string is its stable identity for the deps below.
  const namesKey = searchOrder.join('\u0000');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- namesKey stands in for searchOrder
  const declared = useMemo(() => searchOrder, [namesKey]);

  // The readers to try for `key`, in order, and the id to read from them.
  const lookup = useCallback(
    (key: string): { id: string; candidates: Messages[] } => {
      const sep = key.indexOf(':');
      const scoped = sep !== -1;
      // Look the bundles up directly, not via bundlesFor: a `ns:` prefix for a namespace the caller
      // did not list (or a typo) must fall back to i18next the way react-i18next resolves it, rather
      // than throw during render. The strict bundlesFor check stays at the hook top for the declared
      // namespaces array.
      //
      // A `ns:` prefix is searched in that namespace alone. This is a deliberate divergence from
      // i18next, which applies fallbackNS to a prefixed key too: naming a namespace says where the
      // string lives, and an id that happens to exist under the same name in a shared bundle is a
      // different string, which would be served in place of the right one. Such a key reads the
      // English source instead, which is correct, if untranslated.
      const searched = scoped ? [key.slice(0, sep)] : declared;
      return {
        id: toArbId(scoped ? key.slice(sep + 1) : key),
        candidates: Array.from(new Set(searched.flatMap((ns) => NS_TO_BUNDLES[ns] ?? [])))
          .map((bundle) => readers[bundle])
          .filter((reader): reader is Messages => reader !== undefined),
      };
    },
    [declared, readers]
  );

  const t = useCallback(
    (key: string | string[], args?: TextArgs): string => {
      if (!useArb) {
        return t18(key, args);
      }
      for (const candidateKey of Array.isArray(key) ? key : [key]) {
        const { id, candidates } = lookup(candidateKey);
        for (const reader of candidates) {
          const text = reader.format(id, args);
          if (text) {
            return text;
          }
        }
      }
      return t18(key, args);
    },
    [useArb, lookup, t18]
  );

  const rich = useCallback(
    (key: string, args?: TextArgs): ReactNode | undefined => {
      if (!useArb) {
        return undefined;
      }
      const { id, candidates } = lookup(key);
      for (const reader of candidates) {
        const nodes = reader.formatRich(id, args);
        if (nodes) {
          return nodes;
        }
      }
      return undefined;
    },
    [useArb, lookup]
  );

  return { t, rich, isLoading: useArb && Object.values(readers).some((r) => r.isLoading) };
}
